import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Typography, IconButton, Chip } from '@material-ui/core';
import { CheckRounded, Edit, Redo, Close as CloseIcon } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { getVolunteerProfileHardcodedFieldValues } from '../../../../../../../common/form/task-form/model/get-volunteer-profile-hardcoded-field-values';
import { TaskCompleteIcon } from '../../../../../../../common/icons/TaskCompleteIcon';
import { SnackbarContextProps } from '../../../../../../../global/config/SnackbarContext';
import { useCampfireQuery } from '../../../../../../../global/network/useCampfireQuery';
import { useEndpointFetch } from '../../../../../../../global/network/useEndpointFetch';
import { BypassTaskSubmissionData } from '../../../../../../../global/applicant-shell/SUYPTask';
import { IncomingVolunteerConfirmationDialog } from '../../IncomingVolunteerConfirmationDialog';
import { GET_TASK_FOR_INCOMING_VOLUNTEER } from './invoming-volunteer-task-view-model.gql';
import { UpdatingCompletedTask } from './UpdatingCompletedTask';
import {
  GetTaskForIncomingVolunteer,
  GetTaskForIncomingVolunteerVariables,
} from './__generated__/GetTaskForIncomingVolunteer';
import { CompletedTaskFormFieldList } from '../../../../../../../common/form/task-form/CompletedTaskViewer';
import { deserializeTaskItems } from '../../../../../../admin/tasks/utils';
import { IncomingVolunteerRefereeCheckView } from './IncomingVolunteerRefereeCheckView';
import { IncomingVolunteerEventView } from './IncomingVolunteerEventView';

interface SubmissionData {
  completedTaskId: string;
  status: 'approved' | 'pending' | 'rejected';
}

interface IncomingVolunteerTaskViewProps {
  onClose: () => void;
  taskId: string;
  completedTaskId?: string;
  reload?: () => void;
  reloadList?: () => void;
  selectedUserId: string;
  setSnackbar?: (props: SnackbarContextProps | undefined) => void;
}

export const IncomingVolunteerTaskView = ({
  onClose,
  taskId,
  completedTaskId,
  reload,
  selectedUserId,
}: IncomingVolunteerTaskViewProps) => {
  const [openApproveTaskZeroDialog, setOpenApproveTaskZeroDialog] = useState(false);
  const [openBypassDialog, setOpenBypassDialog] = useState(false);
  const [selectedVolunteerProfileId] = useQueryParam('volunteerProfileId', StringParam);
  const updateCompletedTaskStatus = useEndpointFetch<SubmissionData>(
    '/vm/volunteer/manage/update-completed-task-status'
  );
  const bypassCompletedTask = useEndpointFetch<BypassTaskSubmissionData>('/vm/volunteer/profile/completed-task/bypass');

  const tasksQuery = useCampfireQuery<GetTaskForIncomingVolunteer, GetTaskForIncomingVolunteerVariables>(
    GET_TASK_FOR_INCOMING_VOLUNTEER,
    {
      options: {
        variables: {
          taskId,
          completedTaskId: completedTaskId ?? '',
          profileId: selectedVolunteerProfileId ?? '',
          userId: selectedUserId,
        },
      },
    }
  );

  const handleApproveTask = () => {
    if (completedTaskId) {
      updateCompletedTaskStatus
        .run({
          completedTaskId,
          status: 'approved',
        })
        .then(() => {
          if (reload) {
            reload();
          }
          onClose();
        });
    }
  };

  const onSaveTaskSuccess = () => {
    if (reload) {
      reload();
    }
    if (tasksQuery.refetch) {
      tasksQuery.refetch();
    }
    setIsEditting(false);
  };

  const onRefreshEventData = () => {
    if (reload) {
      reload();
    }
    if (tasksQuery.refetch) {
      tasksQuery.refetch();
    }
  };

  const task = useMemo(() => {
    if (!tasksQuery.data?.vm.task) return null;
    return { ...tasksQuery.data.vm.task, __typename: 'VOLUNTEER_TaskType' as any };
  }, [tasksQuery.data]);

  const taskItems = useMemo(() => {
    return tasksQuery.data?.vm.task?.taskItems;
  }, [tasksQuery.data]);

  const completedTask = useMemo(() => {
    if (!tasksQuery.data) return undefined;
    return tasksQuery.data.vm.completedTask;
  }, [tasksQuery.data]);

  const taskItemValues = useMemo(() => {
    if (!completedTask) return [];
    return completedTask.taskFieldValues.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.field.taskFieldId]: curr,
      }),
      {}
    );
  }, [completedTask]);

  const taskHeaderDetails = useMemo(() => {
    if (!tasksQuery.data?.vm.task) return undefined;
    return {
      title: tasksQuery.data.vm.task.title,
      description: tasksQuery.data.vm.task.description,
      cake: tasksQuery.data.vm.task.cake.title,
    };
  }, [tasksQuery.data]);

  const volunteerProfileHardCodedFieldValues = useMemo(() => {
    if (tasksQuery.data?.vm.completedTask?.task.order !== -1 || !tasksQuery.data.vm.profile) {
      return undefined;
    }
    return getVolunteerProfileHardcodedFieldValues(tasksQuery.data.vm.profile);
  }, [tasksQuery.data]);

  const refereeCheck = useMemo(() => {
    if (!tasksQuery.data?.vm.refereeCheck || !Array.isArray(tasksQuery.data.vm.refereeCheck)) {
      return null;
    }

    const refereeContacts = tasksQuery.data.vm.refereeCheck;
    const completedCount = refereeContacts.filter((contact) => contact.isCompleted).length;
    const totalRequired = Math.max(refereeContacts.length, 2); // Assume at least 2 required or actual count

    return {
      taskRefereeCheckId: '', // Not available in individual contacts
      requiredReferencesCount: totalRequired,
      instructions: '', // Not available from backend currently
      completedSubmissionsCount: completedCount,
      pendingSubmissionsCount: refereeContacts.length - completedCount,
      isComplete: completedCount >= totalRequired,
      dateCreated: refereeContacts.length > 0 ? refereeContacts[0].dateContactProvided : new Date().toISOString(),
      refereeSubmissions: refereeContacts.map((contact) => ({
        refereeSubmissionId: contact.completedTaskRefereeContactId,
        refereeName: contact.refereeName,
        refereeEmail: contact.refereeEmail,
        refereePhone: contact.refereePhone,
        submissionToken: contact.submissionToken,
        dateInvited: contact.dateInvited,
        dateLastReminded: contact.dateLastReminded,
        dateSubmitted: contact.dateSubmitted,
        isCompleted: contact.isCompleted,
        submissionData: contact.submissionData,
      })),
    };
  }, [tasksQuery.data]);

  const isRefereeCheckTask = useMemo(() => {
    return tasksQuery.data?.vm.task?.isRefereeCheckTask || false;
  }, [tasksQuery.data]);

  const isEventTask = useMemo(() => {
    return tasksQuery.data?.vm.task?.isEventTask || false;
  }, [tasksQuery.data]);

  const events = useMemo(() => {
    const rawEvents = tasksQuery.data?.vm.completedTask?.events || [];
    return rawEvents.map((event) => ({
      completedTaskEventId: event.completedTaskEventId,
      startDateTime: event.startDateTime || undefined,
      endDateTime: event.endDateTime || undefined,
      timezone: event.timezone || undefined,
      status: event.status as 'scheduled' | 'completed' | 'cancelled' | 'no_show',
      placesAddress: event.placesAddress
        ? {
            placesId: event.placesAddress.placesId,
            description: event.placesAddress.description,
            formatted: event.placesAddress.formatted,
            latLong: event.placesAddress.latLong
              ? {
                  lat: event.placesAddress.latLong.lat,
                  long: event.placesAddress.latLong.long,
                }
              : undefined,
          }
        : undefined,
      activityLocation: event.activityLocation
        ? {
            activityLocationId: event.activityLocation.activityLocationId,
            name: event.activityLocation.placesAddress?.description || 'Location',
            address: event.activityLocation.placesAddress?.formatted || '',
          }
        : undefined,
      remoteLocation: event.remoteLocation || undefined,
      managers: event.managers || undefined,
      participantFeedback: event.participantFeedback || undefined,
      volunteerMessage: event.message || undefined,
      rescheduleCount: event.rescheduleCount || 0,
      originalScheduledDate: event.originalScheduledDate || undefined,
      dateCreated: event.dateCreated,
    }));
  }, [tasksQuery.data]);

  const volunteerProfile = useMemo(() => {
    if (!tasksQuery.data?.vm.profile) return null;
    return {
      name:
        tasksQuery.data.vm.profile.preferredName ||
        `${tasksQuery.data.vm.profile.firstName} ${tasksQuery.data.vm.profile.lastName}`,
      email: tasksQuery.data.vm.profile.email,
    };
  }, [tasksQuery.data]);

  const completedTasks = useMemo(() => {
    return tasksQuery.data?.vm.profile?.completedTasks || [];
  }, [tasksQuery.data]);

  const showingEditButton = Boolean(completedTaskId) && !isRefereeCheckTask && !isEventTask;
  const [isEditting, setIsEditting] = React.useState(!completedTaskId && !isRefereeCheckTask && !isEventTask);

  return (
    <>
      <Box>
        <Box
          borderBottom={`1px solid #e0e0e0`}
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          px={3}
          py={1}
        >
          <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
            <Typography variant='h6' style={{ fontWeight: 600, fontSize: '16px' }}>
              {taskHeaderDetails?.title || 'Task Review'}
            </Typography>
            {/* Task Type Chips - Only show if task is not completed and data is loaded */}
            {completedTask?.status !== 'approved' && !tasksQuery.loading && tasksQuery.data?.vm.task && (
              <Box display='flex' alignItems='center' style={{ gap: '6px' }}>
                {isRefereeCheckTask && (
                  <Chip
                    label='Referee Check'
                    size='small'
                    style={{
                      backgroundColor: '#f3e5f5',
                      color: '#7b1fa2',
                      fontSize: '0.75rem',
                      fontWeight: 550,
                      borderRadius: '4px',
                    }}
                  />
                )}
                {isEventTask && (
                  <Chip
                    label='Event'
                    size='small'
                    style={{
                      backgroundColor: '#e0f2f1',
                      color: '#00695c',
                      fontSize: '0.75rem',
                      fontWeight: 550,
                      borderRadius: '4px',
                    }}
                  />
                )}
                {!isRefereeCheckTask && !isEventTask && (
                  <Chip
                    label={tasksQuery.data.vm.task.managerCompleted ? 'Manager Completes' : 'User Completes'}
                    size='small'
                    style={{
                      backgroundColor: tasksQuery.data.vm.task.managerCompleted ? '#FFF4E6' : '#E3F2FD',
                      color: tasksQuery.data.vm.task.managerCompleted ? '#E65100' : '#1565C0',
                      fontSize: '0.75rem',
                      fontWeight: 550,
                      borderRadius: '4px',
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
            {tasksQuery.loading ? (
              <TabletButton disabled variant='outlined' color='primary'>
                Loading...
              </TabletButton>
            ) : completedTask?.status === 'approved' ||
              completedTask?.status === 'admin-approved' ||
              completedTask?.status === 'not-applicable' ? (
              <TabletButton disabled variant='outlined' color='primary' endIcon={<TaskCompleteIcon isComplete />}>
                Task approved
              </TabletButton>
            ) : (
              <>
                {/* Determine if task is ready for approval */}
                {(() => {
                  const isRefereeTaskReady = isRefereeCheckTask ? refereeCheck?.isComplete && completedTask : true;
                  const isEventTaskReady = isEventTask
                    ? events.filter((e) => e.status === 'completed').length > 0
                    : true;
                  const isTaskReady =
                    !isRefereeCheckTask && !isEventTask ? true : isRefereeTaskReady && isEventTaskReady;

                  return isTaskReady && completedTaskId ? (
                    /* Approval Button - task is ready and a CompletedTask exists */
                    volunteerProfileHardCodedFieldValues ? (
                      <TabletButton
                        variant='outlined'
                        color='primary'
                        onClick={() => setOpenApproveTaskZeroDialog(true)}
                        disabled={updateCompletedTaskStatus.isLoading}
                        startIcon={<CheckRounded color='primary' />}
                      >
                        Approve task
                      </TabletButton>
                    ) : (
                      <TabletButton
                        variant='outlined'
                        color='primary'
                        onClick={handleApproveTask}
                        disabled={updateCompletedTaskStatus.isLoading}
                        startIcon={<CheckRounded color='primary' />}
                      >
                        Approve task
                      </TabletButton>
                    )
                  ) : (
                    /* Bypass Button - Show when task is not ready for approval */
                    <TabletButton
                      variant='outlined'
                      onClick={() => setOpenBypassDialog(true)}
                      disabled={updateCompletedTaskStatus.isLoading || bypassCompletedTask.isLoading}
                      style={{ borderRadius: 4, padding: '0.5rem 0.5rem', marginLeft: 8 }}
                    >
                      <Redo color='inherit' style={{ fontSize: 14 }} />
                      <Typography style={{ fontSize: 14, marginLeft: 8, fontWeight: 500 }}>Bypass</Typography>
                    </TabletButton>
                  );
                })()}
              </>
            )}
            {showingEditButton ? (
              <TabletButton
                variant='outlined'
                onClick={() => setIsEditting(!isEditting)}
                startIcon={<Edit />}
                style={{ borderRadius: '4px', paddingLeft: '4px', paddingRight: '4px' }}
              >
                Edit
              </TabletButton>
            ) : null}
            <Box marginLeft={1}>
              <IconButton onClick={() => onClose()}>
                <CloseIcon color='action' />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box flex={1} style={{ overflowY: 'scroll', backgroundColor: '#f6f8f9' }} display='flex'>
        <Box position='relative' display='flex' flex='1 1 auto' height={'auto'} minHeight={1}>
          <CircularProgressOverlay isLoading={tasksQuery.loading || updateCompletedTaskStatus.isLoading} />
          {taskHeaderDetails ? (
            <Box paddingX={2} paddingY={2} flex='1 1 auto'>
              {task && isEditting && !isRefereeCheckTask && !isEventTask && (
                <UpdatingCompletedTask
                  task={task as any}
                  taskItemValues={taskItemValues}
                  onCancel={onClose}
                  completedTaskId={completedTask?.completedTaskId}
                  onSaveSuccess={onSaveTaskSuccess}
                  userId={selectedUserId}
                  profile={tasksQuery.data?.vm.profile}
                  programs={tasksQuery.data?.vm.programs.filter((p) => !p.dateSuspended)}
                  completedTasks={completedTasks}
                />
              )}
              {task && isRefereeCheckTask && volunteerProfile ? (
                <IncomingVolunteerRefereeCheckView
                  refereeCheck={refereeCheck}
                  taskId={taskId}
                  profileId={selectedVolunteerProfileId ?? ''}
                  userId={selectedUserId}
                  onRefresh={tasksQuery.refetch}
                  taskItems={tasksQuery.data?.vm.task?.taskItems || []}
                />
              ) : task && isEventTask && volunteerProfile ? (
                <IncomingVolunteerEventView
                  events={events}
                  taskId={taskId}
                  profileId={selectedVolunteerProfileId ?? ''}
                  programId={tasksQuery.data?.vm.profile?.programs?.[0]?.programId}
                  onRefresh={onRefreshEventData}
                  taskItems={taskItems}
                  taskItemValues={taskItemValues}
                />
              ) : task && !isEditting && !isRefereeCheckTask && !isEventTask ? (
                <CompletedTaskFormFieldList
                  taskItems={deserializeTaskItems(taskItems)}
                  taskItemValues={taskItemValues}
                  isTaskZero={!!volunteerProfileHardCodedFieldValues}
                  volunteerProfileValues={volunteerProfileHardCodedFieldValues}
                  completedTasks={completedTasks}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <IncomingVolunteerConfirmationDialog
        title='Approve initial Volunteer Profile?'
        body={`This volunteer will gain access to all other onboarding and general tasks, and will be able to complete the rest of their onboarding. We'll send them an email letting them know that they've been approved.`}
        open={openApproveTaskZeroDialog}
        id={selectedVolunteerProfileId ?? ''}
        disabled={updateCompletedTaskStatus.isLoading}
        handleCloseClick={() => setOpenApproveTaskZeroDialog(false)}
        approveButtonText='Approve initial Volunteer Profile'
        handleApproveClick={() => {
          if (completedTaskId) {
            updateCompletedTaskStatus
              .run({
                completedTaskId,
                status: 'approved',
              })
              .then(() => {
                if (reload) {
                  reload();
                }
                onClose();
              });
          }
        }}
      />

      <IncomingVolunteerConfirmationDialog
        open={openBypassDialog}
        id={selectedVolunteerProfileId ?? ''}
        disabled={bypassCompletedTask.isLoading}
        handleCloseClick={() => setOpenBypassDialog(false)}
        approveButtonText='Mark as complete'
        title={isRefereeCheckTask ? 'Bypass Referee Check' : isEventTask ? 'Bypass Event Task' : 'Bypass Task'}
        body={
          isRefereeCheckTask
            ? 'Are you sure you want to mark this task as complete? This will bypass the referee check requirement and approve the task without all references being completed.'
            : isEventTask
            ? 'Are you sure you want to mark this task as complete? This will bypass the event attendance requirement and approve the task without a completed event.'
            : 'Are you sure you want to mark this task as complete? This will bypass the task requirements and approve it for this volunteer.'
        }
        handleApproveClick={() => {
          bypassCompletedTask
            .run({
              profileId: selectedVolunteerProfileId ?? '',
              taskId,
              status: 'admin-approved',
            })
            .then(() => {
              setOpenBypassDialog(false);
              if (reload) {
                reload();
              }
              onClose();
            });
        }}
      />
    </>
  );
};
